(() => {
  const SELECTOR = '[data-inga-birthday-popup]';

  class IngaBirthdayPopup {
    constructor(element) {
      this.element = element;
      this.dialog = element.querySelector('[role="dialog"]');
      this.closeButton = element.querySelector('[data-birthday-close]');
      this.copyButton = element.querySelector('[data-birthday-copy]');
      this.copyLabel = element.querySelector('[data-birthday-copy-label]');
      this.code = element.dataset.code || 'INGA15';
      this.delay = Number.parseInt(element.dataset.delay, 10) || 0;
      this.storageKey = `inga-birthday-popup-${element.dataset.sectionId}-${this.code}`;
      this.returnFocus = null;
      this.timer = null;
      this.isOpen = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);
      this.copyCode = this.copyCode.bind(this);

      this.closeButton?.addEventListener('click', this.close);
      this.copyButton?.addEventListener('click', this.copyCode);
      this.element.addEventListener('click', (event) => {
        if (event.target === this.element) this.close();
      });

      if (this.shouldShow()) {
        this.timer = window.setTimeout(() => this.open(), Shopify.designMode ? 0 : this.delay);
      }
    }

    shouldShow() {
      if (Shopify.designMode) return true;

      const startAt = Date.parse(this.element.dataset.startAt);
      const endAt = Date.parse(this.element.dataset.endAt);
      const now = Date.now();

      if (!Number.isFinite(startAt) || !Number.isFinite(endAt) || now < startAt || now > endAt) {
        return false;
      }

      if (this.element.dataset.oncePerSession !== 'true') return true;

      try {
        return window.sessionStorage.getItem(this.storageKey) !== 'dismissed';
      } catch (_error) {
        return true;
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.returnFocus = document.activeElement;
      this.element.hidden = false;
      document.body.classList.add('iv-birthday-popup-open');
      document.addEventListener('keydown', this.onKeydown);
      document.dispatchEvent(
        new CustomEvent('inga:birthday-popup:open', { detail: { sectionId: this.element.dataset.sectionId } })
      );

      window.requestAnimationFrame(() => {
        this.element.classList.add('is-visible');
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.element.classList.remove('is-visible');
      document.body.classList.remove('iv-birthday-popup-open');
      document.removeEventListener('keydown', this.onKeydown);

      if (!Shopify.designMode && this.element.dataset.oncePerSession === 'true') {
        try {
          window.sessionStorage.setItem(this.storageKey, 'dismissed');
        } catch (_error) {
          // Storage can be unavailable in privacy modes; the popup still works.
        }
      }

      window.setTimeout(() => {
        this.element.hidden = true;
        if (this.returnFocus instanceof HTMLElement) this.returnFocus.focus({ preventScroll: true });
        document.dispatchEvent(
          new CustomEvent('inga:birthday-popup:close', { detail: { sectionId: this.element.dataset.sectionId } })
        );
      }, 400);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async copyCode() {
      let copied = false;

      try {
        await navigator.clipboard.writeText(this.code);
        copied = true;
      } catch (_error) {
        const field = document.createElement('textarea');
        field.value = this.code;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        copied = document.execCommand('copy');
        field.remove();
      }

      if (!copied || !this.copyButton || !this.copyLabel) return;

      this.copyButton.classList.add('is-copied');
      this.copyLabel.textContent = this.copyButton.dataset.copiedLabel;
      window.clearTimeout(this.copyResetTimer);
      this.copyResetTimer = window.setTimeout(() => {
        this.copyButton.classList.remove('is-copied');
        this.copyLabel.textContent = this.copyButton.dataset.copyLabel;
      }, 2200);
    }

    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.copyResetTimer);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('iv-birthday-popup-open');
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((element) => {
      if (!element.ingaBirthdayPopup) element.ingaBirthdayPopup = new IngaBirthdayPopup(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector(SELECTOR)?.ingaBirthdayPopup?.destroy();
  });
})();
